import React, { ReactNode, useState, useMemo } from "react";
import clsx from "clsx";
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Button } from "./Button";
import { Input } from "./Input";
import { Checkbox } from "./Checkbox";

// Table Types
export interface TableColumn<T = Record<string, unknown>> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (value: unknown, row: T, index: number) => ReactNode;
  width?: string;
  align?: "left" | "center" | "right";
}

export interface TableProps<T = Record<string, unknown>> {
  columns: TableColumn<T>[];
  data: T[];
  striped?: boolean;
  bordered?: boolean;
  hoverable?: boolean;
  compact?: boolean;
  selectable?: boolean;
  sortable?: boolean;
  searchable?: boolean;
  pagination?: boolean;
  pageSize?: number;
  className?: string;
  onRowClick?: (row: T, index: number) => void;
  onSelectionChange?: (selectedRows: T[]) => void;
  actions?: ReactNode;
  footer?: ReactNode;
}

export interface TableHeaderProps {
  children: ReactNode;
  sortable?: boolean;
  sortDirection?: "asc" | "desc" | null;
  onSort?: () => void;
  align?: "left" | "center" | "right";
  className?: string;
}

export interface TableRowProps {
  children: ReactNode;
  active?: boolean;
  hoverable?: boolean;
  disabled?: boolean;
  error?: boolean;
  onClick?: () => void;
  className?: string;
}

export interface TableCellProps {
  children: ReactNode;
  align?: "left" | "center" | "right";
  className?: string;
}

// Table Header Component
export const TableHeader: React.FC<TableHeaderProps> = ({
  children,
  sortable = false,
  sortDirection,
  onSort,
  align = "left",
  className,
}) => {
  const alignStyles = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  };

  return (
    <th
      className={clsx(
        "px-4 py-3 bg-gray-800 text-gray-300 font-semibold text-sm",
        alignStyles[align],
        {
          "cursor-pointer hover:bg-gray-700 transition-colors": sortable,
        },
        className,
      )}
      onClick={sortable ? onSort : undefined}
    >
      <div className="flex items-center gap-2">
        <span>{children}</span>
        {sortable && (
          <div className="flex flex-col">
            <ChevronUp
              className={clsx(
                "w-3 h-3",
                sortDirection === "asc" ? "text-tracq-bright-blue" : "text-gray-500",
              )}
            />
            <ChevronDown
              className={clsx(
                "w-3 h-3 -mt-1",
                sortDirection === "desc" ? "text-tracq-bright-blue" : "text-gray-500",
              )}
            />
          </div>
        )}
      </div>
    </th>
  );
};

// Table Row Component
export const TableRow: React.FC<TableRowProps> = ({
  children,
  active = false,
  hoverable = false,
  disabled = false,
  error = false,
  onClick,
  className,
}) => {
  const rowClasses = clsx(
    "border-b border-gray-700 transition-colors",
    {
      "text-gray-200": !active && !disabled && !error,
      "bg-tracq-bright-blue/10": active,
      "text-gray-500 cursor-not-allowed": disabled,
      "bg-red-900/50 text-red-400": error,
      "hover:bg-gray-700 cursor-pointer": hoverable && !disabled && !active && !error,
    },
    className,
  );

  return (
    <tr className={rowClasses} onClick={onClick}>
      {children}
    </tr>
  );
};

// Table Cell Component
export const TableCell: React.FC<TableCellProps> = ({ children, align = "left", className }) => {
  const alignStyles = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  };

  return <td className={clsx("px-4 py-3 text-sm", alignStyles[align], className)}>{children}</td>;
};

// Main Table Component
export const Table = <T extends Record<string, unknown>>({
  columns,
  data,
  striped = false,
  bordered = false,
  hoverable = false,
  compact = false,
  selectable = false,
  sortable = false,
  searchable = false,
  pagination = false,
  pageSize = 10,
  className,
  onRowClick,
  onSelectionChange,
  actions,
  footer,
}: TableProps<T>) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [data, searchTerm]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      if (typeof aValue === "number" && typeof bValue === "number") {
        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      }

      const aStr = String(aValue ?? "");
      const bStr = String(bValue ?? "");
      if (aStr < bStr) return sortDirection === "asc" ? -1 : 1;
      if (aStr > bStr) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortColumn, sortDirection]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, pageSize, pagination]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(columnKey);
      setSortDirection("asc");
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIndices = new Set(paginatedData.map((_, index) => index));
      setSelectedRows(allIndices);
      onSelectionChange?.(paginatedData);
    } else {
      setSelectedRows(new Set());
      onSelectionChange?.([]);
    }
  };

  const handleSelectRow = (index: number, checked: boolean) => {
    const newSelectedRows = new Set(selectedRows);
    if (checked) {
      newSelectedRows.add(index);
    } else {
      newSelectedRows.delete(index);
    }
    setSelectedRows(newSelectedRows);

    const selectedData = paginatedData.filter((_, i) => newSelectedRows.has(i));
    onSelectionChange?.(selectedData);
  };

  const tableClasses = clsx(
    "w-full border-collapse",
    {
      "border border-gray-700": bordered,
    },
    className,
  );

  const rowClasses = clsx("border-b border-gray-700", {
    "odd:bg-gray-900 even:bg-gray-800": striped,
    "hover:bg-gray-700 cursor-pointer": hoverable,
  });

  const cellClasses = clsx("px-4 text-sm", {
    "py-1": compact,
    "py-3": !compact,
  });

  return (
    <div className="space-y-4">
      {/* Search and Actions */}
      {(searchable || actions) && (
        <div className="flex items-center justify-between gap-4">
          {searchable && (
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          )}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className={tableClasses}>
          {/* Header */}
          <thead>
            <tr className="bg-gray-800">
              {selectable && (
                <th className="px-4 py-3 w-12">
                  <Checkbox
                    checked={selectedRows.size === paginatedData.length && paginatedData.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
              )}
              {columns.map((column) => (
                <TableHeader
                  key={column.key}
                  {...(sortable && column.sortable !== undefined
                    ? { sortable: sortable && column.sortable }
                    : {})}
                  sortDirection={sortColumn === column.key ? sortDirection : null}
                  onSort={() => handleSort(column.key)}
                  {...(column.align ? { align: column.align } : {})}
                >
                  {column.label}
                </TableHeader>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {paginatedData.map((row, index) => (
              <TableRow
                key={index}
                hoverable={hoverable}
                active={selectedRows.has(index)}
                onClick={() => onRowClick?.(row, index)}
                className={rowClasses}
              >
                {selectable && (
                  <TableCell>
                    <Checkbox
                      checked={selectedRows.has(index)}
                      onChange={(checked) => handleSelectRow(index, checked)}
                    />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    {...(column.align !== undefined && { align: column.align })}
                    className={cellClasses}
                  >
                    {column.render
                      ? (column.render(row[column.key], row, index) as ReactNode)
                      : (row[column.key] as ReactNode)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </tbody>

          {/* Footer */}
          {footer && (
            <tfoot>
              <tr className="bg-gray-800">
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-4 py-3">
                  {footer}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Zeige {(currentPage - 1) * pageSize + 1} bis{" "}
            {Math.min(currentPage * pageSize, sortedData.length)} von {sortedData.length} Einträgen
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-gray-300">
              Seite {currentPage} von {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Striped Table Component
export const StripedTable: React.FC<Omit<TableProps, "striped">> = (props) => {
  return <Table {...props} striped />;
};

// Bordered Table Component
export const BorderedTable: React.FC<Omit<TableProps, "bordered">> = (props) => {
  return <Table {...props} bordered />;
};

// Hoverable Table Component
export const HoverableTable: React.FC<Omit<TableProps, "hoverable">> = (props) => {
  return <Table {...props} hoverable />;
};

// Compact Table Component
export const CompactTable: React.FC<Omit<TableProps, "compact">> = (props) => {
  return <Table {...props} compact />;
};

// Selectable Table Component
export const SelectableTable: React.FC<Omit<TableProps, "selectable">> = (props) => {
  return <Table {...props} selectable />;
};

// Responsive Table Component (Card Layout on Mobile)
export const ResponsiveTable: React.FC<TableProps> = ({ columns, data, ...props }) => {
  return (
    <div className="block md:hidden">
      {/* Mobile Card Layout */}
      <div className="space-y-4">
        {data.map((row, index) => (
          <div key={index} className="bg-gray-800 rounded-lg p-4 space-y-2">
            {columns.map((column) => (
              <div key={column.key} className="flex justify-between">
                <span className="text-sm text-gray-400">{column.label}:</span>
                <span className="text-sm text-gray-200">
                  {column.render
                    ? (column.render(row[column.key], row, index) as ReactNode)
                    : (row[column.key] as ReactNode)}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Desktop Table Layout */}
      <div className="hidden md:block">
        <Table columns={columns} data={data} {...props} />
      </div>
    </div>
  );
};

// Data Table Component (with all features)
export const DataTable: React.FC<TableProps> = (props) => {
  return <Table {...props} striped hoverable sortable searchable pagination selectable />;
};
